import React from "react";

/**
 * SiteBackground — fixed, full-viewport biotech-themed decorative layer.
 * - Lives behind all content (z-index: -10, pointer-events: none).
 * - Uses brand HSL tokens so it adapts to light/dark themes automatically.
 * - Pure SVG + CSS, no JS animation cost.
 */
export const SiteBackground = () => {
    return (
        <div
            data-testid="site-background"
            aria-hidden
            className="fixed inset-0 -z-10 overflow-hidden pointer-events-none"
        >
            {/* Soft gradient base — keeps things from looking flat */}
            <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-card/60" />

            {/* Microscopic plant-cell texture — applied site-wide, subtle */}
            <div
                className="absolute inset-0 opacity-[0.14] dark:opacity-[0.10] mix-blend-multiply dark:mix-blend-screen"
                style={{
                    backgroundImage:
                        "url('https://images.unsplash.com/photo-1766713655637-45c84da72c37?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxwbGFudCUyMGNlbGwlMjBtaWNyb3Njb3BpY3xlbnwwfHx8fDE3Nzc2NjMwNDN8MA&ixlib=rb-4.1.0&q=85')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundAttachment: "fixed",
                }}
            />

            {/* Soft wash to dim the texture — keeps content highly readable */}
            <div className="absolute inset-0 bg-background/70 dark:bg-background/75" />

            {/* Top-left cell cluster */}
            <svg
                className="absolute -top-32 -left-24 w-[640px] h-[640px] opacity-[0.55] animate-bg-float-a"
                viewBox="0 0 600 600"
                fill="none"
            >
                <defs>
                    <radialGradient id="cellA" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="hsl(var(--brand-moss))" stopOpacity="0.45" />
                        <stop offset="60%" stopColor="hsl(var(--brand-moss))" stopOpacity="0.10" />
                        <stop offset="100%" stopColor="hsl(var(--brand-moss))" stopOpacity="0" />
                    </radialGradient>
                </defs>
                <circle cx="300" cy="300" r="280" fill="url(#cellA)" />
                {/* Inner organelles */}
                <circle cx="240" cy="260" r="40" fill="hsl(var(--brand-moss))" opacity="0.18" />
                <circle cx="350" cy="350" r="22" fill="hsl(var(--brand-moss))" opacity="0.22" />
                <circle cx="380" cy="240" r="14" fill="hsl(var(--brand-copper))" opacity="0.30" />
            </svg>

            {/* Bottom-right cell cluster */}
            <svg
                className="absolute -bottom-40 -right-32 w-[720px] h-[720px] opacity-[0.45] animate-bg-float-b"
                viewBox="0 0 600 600"
                fill="none"
            >
                <defs>
                    <radialGradient id="cellB" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="hsl(var(--brand-copper))" stopOpacity="0.35" />
                        <stop offset="60%" stopColor="hsl(var(--brand-copper))" stopOpacity="0.08" />
                        <stop offset="100%" stopColor="hsl(var(--brand-copper))" stopOpacity="0" />
                    </radialGradient>
                </defs>
                <circle cx="300" cy="300" r="280" fill="url(#cellB)" />
                <circle cx="220" cy="320" r="32" fill="hsl(var(--brand-copper))" opacity="0.20" />
                <circle cx="380" cy="240" r="18" fill="hsl(var(--brand-copper))" opacity="0.25" />
            </svg>

            {/* Floating DNA strand — left edge */}
            <svg
                className="absolute top-[18%] -left-8 h-[60vh] w-[110px] opacity-[0.22]"
                viewBox="0 0 100 600"
                fill="none"
            >
                <g className="animate-bg-helix">
                    <path
                        d="M30 0 Q 80 75 30 150 Q -20 225 30 300 Q 80 375 30 450 Q -20 525 30 600"
                        stroke="hsl(var(--brand-copper))"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        fill="none"
                    />
                    <path
                        d="M70 0 Q 20 75 70 150 Q 120 225 70 300 Q 20 375 70 450 Q 120 525 70 600"
                        stroke="hsl(var(--brand-moss))"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        fill="none"
                    />
                    {Array.from({ length: 12 }).map((_, i) => {
                        const t = i / 11;
                        const y = t * 600;
                        const sin = Math.sin(t * Math.PI * 4);
                        const x1 = 50 - sin * 20;
                        const x2 = 50 + sin * 20;
                        return (
                            <line
                                key={i}
                                x1={x1}
                                x2={x2}
                                y1={y}
                                y2={y}
                                stroke="hsl(var(--brand-copper))"
                                strokeWidth="1"
                                strokeLinecap="round"
                                opacity="0.5"
                            />
                        );
                    })}
                </g>
            </svg>

            {/* Floating DNA strand — right edge (mirrored) */}
            <svg
                className="absolute top-[42%] -right-6 h-[55vh] w-[100px] opacity-[0.20]"
                viewBox="0 0 100 600"
                fill="none"
            >
                <g className="animate-bg-helix-rev">
                    <path
                        d="M30 0 Q 80 75 30 150 Q -20 225 30 300 Q 80 375 30 450 Q -20 525 30 600"
                        stroke="hsl(var(--brand-moss))"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        fill="none"
                    />
                    <path
                        d="M70 0 Q 20 75 70 150 Q 120 225 70 300 Q 20 375 70 450 Q 120 525 70 600"
                        stroke="hsl(var(--brand-copper))"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        fill="none"
                    />
                </g>
            </svg>

            {/* Drifting leaf vectors */}
            <svg
                className="absolute top-[14%] right-[18%] w-14 h-14 opacity-[0.35] animate-bg-leaf-a text-[hsl(var(--brand-moss))]"
                viewBox="0 0 64 64"
                fill="currentColor"
            >
                <path d="M32 4 C 20 14 12 28 14 44 C 22 50 36 50 46 42 C 56 32 54 14 50 6 C 44 6 38 4 32 4Z" opacity="0.55" />
                <path d="M32 6 C 30 22 30 40 26 50" stroke="hsl(var(--brand-cypress))" strokeWidth="1" fill="none" opacity="0.6" />
            </svg>

            <svg
                className="absolute top-[52%] left-[8%] w-10 h-10 opacity-[0.3] animate-bg-leaf-b text-[hsl(var(--brand-copper))]"
                viewBox="0 0 64 64"
                fill="currentColor"
            >
                <path d="M32 4 C 20 14 12 28 14 44 C 22 50 36 50 46 42 C 56 32 54 14 50 6 C 44 6 38 4 32 4Z" opacity="0.6" />
            </svg>

            <svg
                className="absolute bottom-[18%] left-[34%] w-12 h-12 opacity-[0.30] animate-bg-leaf-c text-[hsl(var(--brand-moss))]"
                viewBox="0 0 64 64"
                fill="currentColor"
            >
                <path d="M32 4 C 20 14 12 28 14 44 C 22 50 36 50 46 42 C 56 32 54 14 50 6 C 44 6 38 4 32 4Z" opacity="0.55" />
            </svg>

            {/* Molecule dots constellation */}
            <svg
                className="absolute top-[28%] left-[42%] w-[260px] h-[260px] opacity-[0.35]"
                viewBox="0 0 260 260"
                fill="none"
            >
                <g className="animate-bg-spin-slow" style={{ transformOrigin: "130px 130px" }}>
                    <circle cx="130" cy="40" r="4" fill="hsl(var(--brand-copper))" />
                    <circle cx="220" cy="130" r="4" fill="hsl(var(--brand-moss))" />
                    <circle cx="130" cy="220" r="4" fill="hsl(var(--brand-copper))" />
                    <circle cx="40" cy="130" r="4" fill="hsl(var(--brand-moss))" />
                    <line x1="130" y1="40" x2="220" y2="130" stroke="hsl(var(--brand-copper))" strokeWidth="0.6" strokeDasharray="2 4" opacity="0.7" />
                    <line x1="220" y1="130" x2="130" y2="220" stroke="hsl(var(--brand-moss))" strokeWidth="0.6" strokeDasharray="2 4" opacity="0.7" />
                    <line x1="130" y1="220" x2="40" y2="130" stroke="hsl(var(--brand-copper))" strokeWidth="0.6" strokeDasharray="2 4" opacity="0.7" />
                    <line x1="40" y1="130" x2="130" y2="40" stroke="hsl(var(--brand-moss))" strokeWidth="0.6" strokeDasharray="2 4" opacity="0.7" />
                </g>
            </svg>

            {/* Faint grid/noise overlay for texture */}
            <div className="absolute inset-0 opacity-[0.04] mix-blend-multiply dark:mix-blend-screen"
                style={{
                    backgroundImage:
                        'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'160\' height=\'160\'><filter id=\'n\'><feTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'2\' stitchTiles=\'stitch\'/></filter><rect width=\'100%\' height=\'100%\' filter=\'url(%23n)\' opacity=\'0.7\'/></svg>")',
                }}
            />
        </div>
    );
};

export default SiteBackground;
